/*
 * OpenAI Ads (ChatGPT) — storefront measurement + oppref attribution.
 *
 * Loaded deferred, AFTER pubsub.js/constants.js. Everything here runs ONLY once
 * the consent-gated pixel has initialised: the inline head bootstrap dispatches
 * "oaiq:ready" after Shopify marketing consent is satisfied (granted, or the
 * visitor's region does not require it). So all measurement AND the Piece-3
 * oppref cart write are consent-gated by construction. Dependency-free plain JS.
 *
 * Events (API: oaiq("measure","<name>",{type:"contents",amount,currency,contents})):
 *   page_viewed      once per page load
 *   contents_viewed  product pages, for the selected variant
 *   items_added      every add-to-cart path (shared "cart-update" PubSub event)
 *   checkout_started checkout button on the drawer and the cart page
 * Amounts are INTEGERS IN MINOR UNITS; currency is the ACTIVE presentment currency.
 */
(function () {
  "use strict";

  function ready(cb) {
    if (window.__oaiqReady) cb();
    else document.addEventListener("oaiq:ready", cb, { once: true });
  }

  function measure() {
    if (typeof window.oaiq === "function") window.oaiq.apply(null, arguments);
  }

  // Active presentment currency (e.g. "USD", "EUR"), never a hardcoded default.
  function activeCurrency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || null;
  }

  function toInt(n) {
    n = parseInt(n, 10);
    return isNaN(n) ? 0 : n;
  }

  /* ---- lightweight cart cache (feeds checkout_started; kept current) ---- */
  var cart = null;
  function refreshCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (c) { cart = c; return c; })
      .catch(function () { return null; });
  }

  /* ---- page_viewed ----
   * The SDK does not auto-fire page_viewed (the name appears only in its
   * event-type map, never in an internal measure call), so we fire it once. */
  function firePageViewed() {
    measure("measure", "page_viewed");
  }

  /* ---- contents_viewed (product pages) ----
   * Reads the Liquid-rendered per-variant presentment price map. */
  function fireContentsViewed() {
    var el = document.getElementById("oaiq-product-data");
    if (!el) return; // not a product page
    var data;
    try { data = JSON.parse(el.textContent); } catch (e) { return; }
    if (!data || data.selectedVariantId == null) return;
    var match = (data.variants || []).filter(function (v) {
      return v.id === data.selectedVariantId;
    })[0];
    measure("measure", "contents_viewed", {
      type: "contents",
      amount: match ? toInt(match.amount) : 0,
      currency: data.currency || activeCurrency(),
      contents: [{
        id: data.selectedVariantId,
        name: data.productTitle,
        content_type: "product",
        quantity: 1
      }]
    });
  }

  /* ---- items_added (every add path funnels through the cart-update event) ---- */
  function setupItemsAdded() {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // Only genuine adds. product-form.js emits source "product-form" for ALL
      // add paths; cart.js re-emits source "cart-items" on qty/remove — ignore it.
      if (!event || event.source !== "product-form") return;
      var item = event.cartData;
      if (!item) return;
      var qty = toInt(event.quantity) || toInt(item.quantity) || 1;
      var unit = toInt(item.final_price != null ? item.final_price : item.price);
      measure("measure", "items_added", {
        type: "contents",
        amount: unit * qty,
        currency: activeCurrency(),
        contents: [{
          id: item.variant_id || item.id || event.productVariantId,
          name: item.product_title || item.title,
          content_type: "product",
          quantity: qty
        }]
      });
      refreshCart(); // an add changed the cart; keep the checkout_started cache fresh
    });
  }

  /* ---- checkout_started (drawer + cart page share name="checkout") ---- */
  function setupCheckoutStarted() {
    var fired = false;
    document.addEventListener("click", function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[name="checkout"]');
      if (!btn || fired) return;
      fired = true;
      var c = cart;
      if (!c || !c.items) return;
      measure("measure", "checkout_started", {
        type: "contents",
        amount: toInt(c.total_price),
        currency: c.currency || activeCurrency(),
        contents: c.items.map(function (it) {
          return {
            id: it.variant_id || it.id,
            name: it.product_title || it.title,
            content_type: "product",
            quantity: toInt(it.quantity)
          };
        })
      });
    }, true); // capture: fire before the button's own submit/navigation
  }

  /* ---- Piece 3: carry oppref onto the cart (consent-gated by ready()) ---- */
  function getCookie(name) {
    var m = document.cookie.match(
      "(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)"
    );
    return m ? decodeURIComponent(m[1]) : null;
  }
  function opprefValue() {
    var url = null;
    try { url = new URLSearchParams(window.location.search).get("oppref"); } catch (e) {}
    return url || getCookie("__oppref"); // URL first: SDK may not have written the cookie yet
  }
  function pushOppref() {
    var val = opprefValue();
    if (!val) return;                                          // no value -> do nothing, no fetch
    try {
      if (sessionStorage.getItem("__oaiq_oppref_sent") === val) return; // already handled this session
    } catch (e) {}
    var have = cart ? Promise.resolve(cart) : refreshCart();
    have.then(function (c) {
      if (c && c.attributes && c.attributes.oppref === val) {  // already on the cart
        try { sessionStorage.setItem("__oaiq_oppref_sent", val); } catch (e) {}
        return;
      }
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes: { oppref: val } })
      }).then(function () {
        try { sessionStorage.setItem("__oaiq_oppref_sent", val); } catch (e) {}
      }).catch(function () {});
    });
  }

  /* ---- boot (only after the consent-gated pixel is ready) ---- */
  ready(function () {
    firePageViewed();
    fireContentsViewed();
    setupItemsAdded();
    refreshCart().then(setupCheckoutStarted);
    var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 1); };
    idle(pushOppref);
  });
})();
